import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, Plus, Trash2, ArrowUp, ArrowDown, Save, Upload,
  FileText, PlayCircle, FileDown, Download, ExternalLink, ListChecks, ClipboardCheck, Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { CATEGORY_LABELS } from "@/pages/academy/Academy";

const CATEGORIES = Object.keys(CATEGORY_LABELS);
const ROLES = [
  { value: "berater", label: "Berater" },
  { value: "trainee", label: "Trainee" },
  { value: "manager", label: "Manager" },
  { value: "backoffice", label: "Backoffice" },
  { value: "compliance", label: "Compliance" },
  { value: "admin", label: "Admin" },
];

const LESSON_TYPES = [
  { value: "text", label: "Text", icon: FileText },
  { value: "video", label: "Video (URL)", icon: PlayCircle },
  { value: "pdf", label: "PDF", icon: FileDown },
  { value: "download", label: "Download", icon: Download },
  { value: "link", label: "Externer Link", icon: ExternalLink },
  { value: "checklist", label: "Checkliste", icon: ListChecks },
];

const EMPTY_COURSE = {
  title: { de: "", en: "" },
  description: { de: "", en: "" },
  category: "unternehmen",
  level: "basis",
  duration_minutes: 30,
  is_mandatory: false,
  published: false,
  archived: false,
  assigned_roles: [],
  assigned_users: [],
  modules: [],
  quiz: { pass_score: 70, max_attempts: null, questions: [] },
};

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`);

function moveItem(arr, idx, dir) {
  const next = [...arr];
  const target = idx + dir;
  if (target < 0 || target >= next.length) return arr;
  [next[idx], next[target]] = [next[target], next[idx]];
  return next;
}

/* Upload helper component: uploads to /files and stores file_id on the lesson */
function LessonFileUpload({ lesson, onUploaded, t }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("area", "academy");
    form.append("description", "Kursmaterial");
    try {
      setBusy(true);
      const res = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(res.data);
      toast.success(t("Datei hochgeladen", "File uploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("Upload fehlgeschlagen", "Upload failed"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" className="hidden" onChange={handleFile} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.zip,.txt,.csv" />
      <Button type="button" variant="outline" size="sm" className="rounded-[8px]" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="lesson-upload-button">
        <Upload className="h-3.5 w-3.5 mr-1.5" /> {busy ? t("L\u00e4dt\u2026", "Uploading\u2026") : lesson.file_id ? t("Datei ersetzen", "Replace file") : t("Datei hochladen", "Upload file")}
      </Button>
      {lesson.file_name && <span className="text-xs text-muted-foreground truncate max-w-[220px]">{lesson.file_name}</span>}
    </div>
  );
}

function LessonEditor({ lesson, onChange, onRemove, onMove, t }) {
  const set = (key, value) => onChange({ ...lesson, [key]: value });
  const setL = (key, value) => onChange({ ...lesson, [key]: { ...(lesson[key] || {}), de: value } });
  const TypeIcon = (LESSON_TYPES.find((x) => x.value === lesson.type) || LESSON_TYPES[0]).icon;

  return (
    <div className="rounded-[12px] border border-border bg-white p-4 space-y-3" data-testid={`lesson-editor-${lesson.id}`}>
      <div className="flex items-center gap-2">
        <TypeIcon className="h-4 w-4 text-[#A8905A] shrink-0" />
        <Input
          value={lesson.title?.de || ""}
          onChange={(e) => setL("title", e.target.value)}
          className="rounded-[8px] text-sm h-9"
          placeholder={t("Lektionstitel", "Lesson title")}
          data-testid="lesson-title-input"
        />
        <Select value={lesson.type || "text"} onValueChange={(v) => set("type", v)}>
          <SelectTrigger className="rounded-[8px] w-[170px] h-9 shrink-0" data-testid="lesson-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LESSON_TYPES.map((lt) => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="number"
          value={lesson.duration_minutes || 8}
          onChange={(e) => set("duration_minutes", parseInt(e.target.value) || 5)}
          className="rounded-[8px] w-[70px] h-9 shrink-0 text-sm"
          title={t("Dauer (Min.)", "Duration (min)")}
        />
        <div className="flex shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px] text-destructive" onClick={onRemove} data-testid="lesson-remove-button"><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {lesson.type === "video" && (
        <Input
          value={lesson.video_url || ""}
          onChange={(e) => set("video_url", e.target.value)}
          className="rounded-[8px] text-sm"
          placeholder="Video-URL (YouTube, Vimeo oder MP4)"
          data-testid="lesson-video-url-input"
        />
      )}
      {(lesson.type === "pdf" || lesson.type === "download") && (
        <LessonFileUpload
          lesson={lesson}
          t={t}
          onUploaded={(meta) => onChange({ ...lesson, file_id: meta.id, file_name: meta.filename })}
        />
      )}
      {lesson.type === "link" && (
        <Input
          value={lesson.external_url || ""}
          onChange={(e) => set("external_url", e.target.value)}
          className="rounded-[8px] text-sm"
          placeholder="https://\u2026"
          data-testid="lesson-external-url-input"
        />
      )}
      {lesson.type === "checklist" && (
        <div className="space-y-2">
          {(lesson.items || []).map((item, ii) => (
            <div key={item.id} className="flex items-center gap-2">
              <Input
                value={item.label?.de || ""}
                onChange={(e) => {
                  const items = [...lesson.items];
                  items[ii] = { ...item, label: { ...(item.label || {}), de: e.target.value } };
                  set("items", items);
                }}
                className="rounded-[8px] text-sm h-8"
                placeholder={t("Checklistenpunkt", "Checklist item")}
              />
              <label className="flex items-center gap-1.5 text-xs shrink-0 text-muted-foreground">
                <Checkbox checked={item.required !== false} onCheckedChange={(c) => {
                  const items = [...lesson.items];
                  items[ii] = { ...item, required: !!c };
                  set("items", items);
                }} /> {t("Pflicht", "Required")}
              </label>
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive shrink-0" onClick={() => set("items", lesson.items.filter((_, i) => i !== ii))}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="rounded-[8px] text-xs" onClick={() => set("items", [...(lesson.items || []), { id: uid(), label: { de: "" }, required: true }])} data-testid="checklist-add-item">
            <Plus className="h-3 w-3 mr-1" /> {t("Punkt hinzuf\u00fcgen", "Add item")}
          </Button>
        </div>
      )}
      <Textarea
        rows={lesson.type === "text" ? 4 : 2}
        value={lesson.content?.de || ""}
        onChange={(e) => setL("content", e.target.value)}
        className="rounded-[8px] text-sm"
        placeholder={lesson.type === "text" ? t("Lektionsinhalt", "Lesson content") : t("Begleittext (optional)", "Accompanying text (optional)")}
        data-testid="lesson-content-input"
      />
    </div>
  );
}

function QuestionEditor({ q, qi, onChange, onRemove, onMove, t }) {
  const qtype = q.qtype || "single";
  const set = (key, value) => onChange({ ...q, [key]: value });
  return (
    <div className="rounded-[12px] border border-border bg-white p-4 space-y-3" data-testid={`question-editor-${qi}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground shrink-0">{qi + 1}.</span>
        <Input
          value={q.question?.de || ""}
          onChange={(e) => set("question", { ...(q.question || {}), de: e.target.value })}
          className="rounded-[8px] text-sm h-9"
          placeholder={t("Frage", "Question")}
          data-testid="question-text-input"
        />
        <Select value={qtype} onValueChange={(v) => set("qtype", v)}>
          <SelectTrigger className="rounded-[8px] w-[160px] h-9 shrink-0" data-testid="question-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="single">Single Choice</SelectItem>
            <SelectItem value="multiple">Multiple Choice</SelectItem>
            <SelectItem value="text">{t("Freitext", "Free text")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => onMove(-1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => onMove(1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px] text-destructive" onClick={onRemove}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      {qtype !== "text" && (
        <div className="space-y-2">
          {(q.options || []).map((opt, oi) => (
            <div key={oi} className="flex items-center gap-2">
              {qtype === "single" ? (
                <input
                  type="radio"
                  name={`correct-${q.id}`}
                  checked={q.correct_index === oi}
                  onChange={() => set("correct_index", oi)}
                  className="h-4 w-4 accent-[#0B1220] shrink-0"
                  title={t("Richtige Antwort", "Correct answer")}
                  data-testid={`question-${qi}-correct-${oi}`}
                />
              ) : (
                <Checkbox
                  checked={(q.correct_indices || []).includes(oi)}
                  onCheckedChange={(c) => {
                    const cur = q.correct_indices || [];
                    set("correct_indices", c ? [...cur, oi] : cur.filter((x) => x !== oi));
                  }}
                  data-testid={`question-${qi}-correct-${oi}`}
                />
              )}
              <Input
                value={opt?.de || ""}
                onChange={(e) => {
                  const options = [...q.options];
                  options[oi] = { ...(opt || {}), de: e.target.value };
                  set("options", options);
                }}
                className="rounded-[8px] text-sm h-8"
                placeholder={`${t("Antwort", "Answer")} ${oi + 1}`}
              />
              <Button variant="ghost" size="icon" className="h-7 w-7 rounded-[8px] text-destructive shrink-0" onClick={() => {
                const options = q.options.filter((_, i) => i !== oi);
                const upd = { ...q, options };
                if (qtype === "single" && q.correct_index === oi) upd.correct_index = 0;
                if (qtype === "multiple") upd.correct_indices = (q.correct_indices || []).filter((x) => x !== oi).map((x) => (x > oi ? x - 1 : x));
                onChange(upd);
              }}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="rounded-[8px] text-xs" onClick={() => set("options", [...(q.options || []), { de: "" }])} data-testid={`question-${qi}-add-option`}>
            <Plus className="h-3 w-3 mr-1" /> {t("Antwortoption", "Answer option")}
          </Button>
        </div>
      )}
      {qtype === "text" && (
        <p className="text-xs text-muted-foreground">{t("Freitextantworten werden gespeichert, z\u00e4hlen aber nicht in die Bewertung.", "Free-text answers are stored but do not count towards the score.")}</p>
      )}
    </div>
  );
}

export default function CourseBuilder() {
  const { courseId } = useParams();
  const isNew = courseId === "neu";
  const navigate = useNavigate();
  const { t, tt } = useLang();
  const [course, setCourse] = useState(null);
  const [users, setUsers] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const usersRes = await api.get("/admin/users");
        setUsers(usersRes.data || []);
        if (isNew) {
          setCourse(JSON.parse(JSON.stringify(EMPTY_COURSE)));
        } else {
          const res = await api.get("/admin/courses");
          const found = (res.data || []).find((c) => c.id === courseId);
          if (!found) {
            toast.error(t("Kurs nicht gefunden", "Course not found"));
            navigate("/admin/kurse");
            return;
          }
          found.quiz = found.quiz || { pass_score: 70, max_attempts: null, questions: [] };
          found.assigned_roles = found.assigned_roles || [];
          found.assigned_users = found.assigned_users || [];
          setCourse(found);
          api.get(`/admin/courses/${courseId}/progress`).then((r) => setProgress(r.data)).catch(() => {});
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !course) {
    return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-96 rounded-[16px]" /></div>;
  }

  const setField = (key, value) => setCourse((c) => ({ ...c, [key]: value }));

  const normalizeBilingual = (obj) => ({ de: obj?.de || "", en: obj?.en || obj?.de || "" });

  const save = async () => {
    if (!course.title?.de?.trim()) {
      toast.error(t("Titel erforderlich", "Title required"));
      return;
    }
    const payload = JSON.parse(JSON.stringify(course));
    delete payload.id; delete payload.created_at; delete payload.updated_at; delete payload.enrollment;
    payload.title = normalizeBilingual(payload.title);
    payload.description = normalizeBilingual(payload.description);
    payload.modules = (payload.modules || []).map((m) => ({
      ...m,
      id: m.id || uid(),
      title: normalizeBilingual(m.title),
      lessons: (m.lessons || []).map((l) => ({
        ...l,
        id: l.id || uid(),
        title: normalizeBilingual(l.title),
        content: normalizeBilingual(l.content),
        items: (l.items || []).map((it) => ({ ...it, id: it.id || uid(), label: normalizeBilingual(it.label) })),
      })),
    }));
    if (payload.quiz) {
      payload.quiz.pass_score = parseInt(payload.quiz.pass_score) || 70;
      payload.quiz.max_attempts = payload.quiz.max_attempts ? parseInt(payload.quiz.max_attempts) : null;
      payload.quiz.questions = (payload.quiz.questions || []).map((q) => ({
        ...q,
        id: q.id || uid(),
        question: normalizeBilingual(q.question),
        options: (q.options || []).map(normalizeBilingual),
      }));
    }
    try {
      setSaving(true);
      if (isNew) {
        const res = await api.post("/admin/courses", payload);
        toast.success(t("Kurs erstellt", "Course created"));
        navigate(`/admin/kurse/${res.data.id}`, { replace: true });
      } else {
        await api.patch(`/admin/courses/${course.id}`, payload);
        toast.success(t("Kurs gespeichert", "Course saved"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Fehler beim Speichern", "Error saving"));
    } finally {
      setSaving(false);
    }
  };

  const totalLessons = (course.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0);

  return (
    <div className="space-y-6" data-testid="course-builder">
      <div>
        <Link to="/admin/kurse" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="builder-back-link">
          <ChevronLeft className="h-4 w-4" /> {t("Kursverwaltung", "Course management")}
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">
              {isNew ? t("Neuer Kurs", "New course") : tt(course.title)}
            </h1>
            <div className="mt-1.5 flex items-center gap-2">
              <Badge variant={course.published ? "default" : "outline"} className={`rounded-full text-[10px] ${course.published ? "bg-emerald-600" : ""}`}>
                {course.published ? t("Ver\u00f6ffentlicht", "Published") : t("Entwurf", "Draft")}
              </Badge>
              {course.archived && <Badge variant="outline" className="rounded-full text-[10px]">{t("Archiviert", "Archived")}</Badge>}
              <span className="text-xs text-muted-foreground">{totalLessons} {t("Lektionen", "lessons")} {"\u00b7"} {(course.quiz?.questions || []).length} {t("Quizfragen", "quiz questions")}</span>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="rounded-[12px] bg-[#0B1220]" data-testid="builder-save-button">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? t("Speichert\u2026", "Saving\u2026") : t("Speichern", "Save")}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="grunddaten">
        <TabsList className="rounded-[12px]">
          <TabsTrigger value="grunddaten" className="rounded-[10px]" data-testid="tab-grunddaten">{t("Grunddaten", "Basics")}</TabsTrigger>
          <TabsTrigger value="inhalte" className="rounded-[10px]" data-testid="tab-inhalte">{t("Module & Lektionen", "Modules & lessons")}</TabsTrigger>
          <TabsTrigger value="quiz" className="rounded-[10px]" data-testid="tab-quiz">{t("Wissenstest", "Quiz")}</TabsTrigger>
          <TabsTrigger value="zuweisung" className="rounded-[10px]" data-testid="tab-zuweisung">{t("Zuweisung", "Assignment")}</TabsTrigger>
          {!isNew && <TabsTrigger value="fortschritt" className="rounded-[10px]" data-testid="tab-fortschritt">{t("Fortschritt", "Progress")}</TabsTrigger>}
        </TabsList>

        {/* -------- Basics -------- */}
        <TabsContent value="grunddaten" className="mt-5">
          <Card className="rounded-[16px]">
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Titel (DE)", "Title (DE)")}</Label>
                  <Input value={course.title?.de || ""} onChange={(e) => setField("title", { ...course.title, de: e.target.value })} className="rounded-[10px]" data-testid="course-title-de-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Titel (EN, optional)", "Title (EN, optional)")}</Label>
                  <Input value={course.title?.en || ""} onChange={(e) => setField("title", { ...course.title, en: e.target.value })} className="rounded-[10px]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Beschreibung (DE)", "Description (DE)")}</Label>
                  <Textarea rows={3} value={course.description?.de || ""} onChange={(e) => setField("description", { ...course.description, de: e.target.value })} className="rounded-[10px]" data-testid="course-description-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Beschreibung (EN, optional)", "Description (EN, optional)")}</Label>
                  <Textarea rows={3} value={course.description?.en || ""} onChange={(e) => setField("description", { ...course.description, en: e.target.value })} className="rounded-[10px]" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>{t("Kategorie", "Category")}</Label>
                  <Select value={course.category} onValueChange={(v) => setField("category", v)}>
                    <SelectTrigger className="rounded-[10px]" data-testid="course-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{tt(CATEGORY_LABELS[cat])}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Level</Label>
                  <Select value={course.level} onValueChange={(v) => setField("level", v)}>
                    <SelectTrigger className="rounded-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basis">Basis</SelectItem>
                      <SelectItem value="fortgeschritten">{t("Fortgeschritten", "Advanced")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("Dauer (Min.)", "Duration (min.)")}</Label>
                  <Input type="number" value={course.duration_minutes} onChange={(e) => setField("duration_minutes", parseInt(e.target.value) || 0)} className="rounded-[10px]" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-8 pt-1">
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={course.is_mandatory} onCheckedChange={(v) => setField("is_mandatory", v)} data-testid="course-mandatory-switch" />
                  {t("Pflichtschulung", "Mandatory training")}
                </label>
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={course.published} onCheckedChange={(v) => setField("published", v)} data-testid="course-published-switch" />
                  {t("Ver\u00f6ffentlicht", "Published")}
                </label>
                <label className="flex items-center gap-2.5 text-sm">
                  <Switch checked={!!course.archived} onCheckedChange={(v) => setField("archived", v)} data-testid="course-archived-switch" />
                  {t("Archiviert", "Archived")}
                </label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Modules & lessons -------- */}
        <TabsContent value="inhalte" className="mt-5 space-y-4">
          {(course.modules || []).map((m, mi) => (
            <Card key={m.id || mi} className="rounded-[16px]" data-testid={`module-card-${mi}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground shrink-0">{t("Modul", "Module")} {mi + 1}</span>
                  <Input
                    value={m.title?.de || ""}
                    onChange={(e) => {
                      const modules = [...course.modules];
                      modules[mi] = { ...m, title: { ...(m.title || {}), de: e.target.value } };
                      setField("modules", modules);
                    }}
                    className="rounded-[10px] font-medium"
                    placeholder={t("Modultitel", "Module title")}
                    data-testid={`module-title-${mi}`}
                  />
                  <div className="flex shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => setField("modules", moveItem(course.modules, mi, -1))}><ArrowUp className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px]" onClick={() => setField("modules", moveItem(course.modules, mi, 1))}><ArrowDown className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-[8px] text-destructive" onClick={() => setField("modules", course.modules.filter((_, i) => i !== mi))} data-testid={`module-remove-${mi}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="space-y-3 pl-2 border-l-2 border-[#D6C08D]/30 ml-1">
                  {(m.lessons || []).map((l, li) => (
                    <LessonEditor
                      key={l.id || li}
                      lesson={l}
                      t={t}
                      onChange={(nl) => {
                        const modules = [...course.modules];
                        const lessons = [...m.lessons];
                        lessons[li] = nl;
                        modules[mi] = { ...m, lessons };
                        setField("modules", modules);
                      }}
                      onRemove={() => {
                        const modules = [...course.modules];
                        modules[mi] = { ...m, lessons: m.lessons.filter((_, i) => i !== li) };
                        setField("modules", modules);
                      }}
                      onMove={(dir) => {
                        const modules = [...course.modules];
                        modules[mi] = { ...m, lessons: moveItem(m.lessons, li, dir) };
                        setField("modules", modules);
                      }}
                    />
                  ))}
                  <Button
                    variant="outline" size="sm" className="rounded-[10px]"
                    onClick={() => {
                      const modules = [...course.modules];
                      modules[mi] = { ...m, lessons: [...(m.lessons || []), { id: uid(), title: { de: "" }, type: "text", content: { de: "" }, duration_minutes: 8 }] };
                      setField("modules", modules);
                    }}
                    data-testid={`module-add-lesson-${mi}`}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> {t("Lektion hinzuf\u00fcgen", "Add lesson")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          <Button
            variant="outline" className="rounded-[12px] w-full border-dashed"
            onClick={() => setField("modules", [...(course.modules || []), { id: uid(), title: { de: `Modul ${(course.modules || []).length + 1}` }, lessons: [] }])}
            data-testid="course-add-module"
          >
            <Plus className="h-4 w-4 mr-1.5" /> {t("Modul hinzuf\u00fcgen", "Add module")}
          </Button>
        </TabsContent>

        {/* -------- Quiz -------- */}
        <TabsContent value="quiz" className="mt-5 space-y-4">
          <Card className="rounded-[16px]">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2.5">
                  <ClipboardCheck className="h-4 w-4 text-[#A8905A]" />
                  <Label className="text-sm">{t("Bestehensgrenze (%)", "Passing threshold (%)")}</Label>
                  <Input
                    type="number" min={0} max={100}
                    value={course.quiz?.pass_score ?? 70}
                    onChange={(e) => setField("quiz", { ...course.quiz, pass_score: parseInt(e.target.value) || 0 })}
                    className="rounded-[10px] w-[90px]"
                    data-testid="quiz-pass-score-input"
                  />
                </div>
                <div className="flex items-center gap-2.5">
                  <Label className="text-sm">{t("Max. Versuche (leer = unbegrenzt)", "Max attempts (empty = unlimited)")}</Label>
                  <Input
                    type="number" min={1}
                    value={course.quiz?.max_attempts ?? ""}
                    onChange={(e) => setField("quiz", { ...course.quiz, max_attempts: e.target.value ? parseInt(e.target.value) : null })}
                    className="rounded-[10px] w-[90px]"
                    data-testid="quiz-max-attempts-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          {(course.quiz?.questions || []).map((q, qi) => (
            <QuestionEditor
              key={q.id || qi}
              q={q} qi={qi} t={t}
              onChange={(nq) => {
                const questions = [...course.quiz.questions];
                questions[qi] = nq;
                setField("quiz", { ...course.quiz, questions });
              }}
              onRemove={() => setField("quiz", { ...course.quiz, questions: course.quiz.questions.filter((_, i) => i !== qi) })}
              onMove={(dir) => setField("quiz", { ...course.quiz, questions: moveItem(course.quiz.questions, qi, dir) })}
            />
          ))}
          <Button
            variant="outline" className="rounded-[12px] w-full border-dashed"
            onClick={() => setField("quiz", { ...(course.quiz || { pass_score: 70 }), questions: [...(course.quiz?.questions || []), { id: uid(), qtype: "single", question: { de: "" }, options: [{ de: "" }, { de: "" }], correct_index: 0 }] })}
            data-testid="quiz-add-question"
          >
            <Plus className="h-4 w-4 mr-1.5" /> {t("Frage hinzuf\u00fcgen", "Add question")}
          </Button>
        </TabsContent>

        {/* -------- Assignment -------- */}
        <TabsContent value="zuweisung" className="mt-5">
          <Card className="rounded-[16px]">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-sm font-semibold">{t("Rollen-Zuweisung", "Role assignment")}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{t("Keine Auswahl = Kurs ist f\u00fcr alle sichtbar.", "No selection = course visible to everyone.")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {ROLES.map((r) => {
                    const active = (course.assigned_roles || []).includes(r.value);
                    return (
                      <button
                        key={r.value}
                        onClick={() => setField("assigned_roles", active ? course.assigned_roles.filter((x) => x !== r.value) : [...(course.assigned_roles || []), r.value])}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "bg-[#0B1220] text-white border-transparent" : "border-border hover:bg-muted"}`}
                        data-testid={`assign-role-${r.value}`}
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">{t("Einzelne Mitarbeiter", "Individual employees")}</Label>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto pr-1">
                  {users.map((u) => {
                    const active = (course.assigned_users || []).includes(u.id);
                    return (
                      <label key={u.id} className={`flex items-center gap-2.5 rounded-[10px] border px-3 py-2.5 cursor-pointer text-sm transition-colors ${active ? "border-[#D6C08D]/60 bg-[#D6C08D]/5" : "border-border hover:bg-muted/50"}`}>
                        <Checkbox
                          checked={active}
                          onCheckedChange={(c) => setField("assigned_users", c ? [...(course.assigned_users || []), u.id] : course.assigned_users.filter((x) => x !== u.id))}
                          data-testid={`assign-user-${u.id}`}
                        />
                        <span className="truncate">{u.first_name} {u.last_name}</span>
                        <Badge variant="secondary" className="rounded-full text-[9px] ml-auto shrink-0">{u.role}</Badge>
                      </label>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* -------- Progress -------- */}
        {!isNew && (
          <TabsContent value="fortschritt" className="mt-5">
            <Card className="rounded-[16px]">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-4 w-4 text-[#A8905A]" />
                  <h3 className="text-sm font-semibold">{t("Teilnehmer-Fortschritt", "Participant progress")}</h3>
                </div>
                {!progress || progress.participants.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("Noch keine Teilnehmer.", "No participants yet.")}</p>
                ) : (
                  <div className="divide-y divide-border" data-testid="course-progress-list">
                    {progress.participants.map((p) => (
                      <div key={p.user_id} className="py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{p.user_name}</p>
                          <p className="text-xs text-muted-foreground">{p.role}</p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <span className="font-mono-num text-sm">{p.progress_pct}%</span>
                          {p.quiz_score !== null && p.quiz_score !== undefined && (
                            <span className={`text-xs font-medium ${p.quiz_passed ? "text-emerald-600" : "text-red-600"}`}>
                              Quiz: {p.quiz_score}% ({p.quiz_attempts} {t("Versuche", "attempts")})
                            </span>
                          )}
                          {p.completed ? (
                            <Badge className="rounded-full text-[10px] bg-emerald-600">{t("Abgeschlossen", "Completed")}</Badge>
                          ) : (
                            <Badge variant="outline" className="rounded-full text-[10px]">{t("In Bearbeitung", "In progress")}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
